import { AddCircleOutline, InterpreterModeOutlined } from '@mui/icons-material';
import RateReviewOutlined from '@mui/icons-material/RateReviewOutlined';
import { Tooltip, IconButton, Box, Typography } from '@mui/material';
import { CollapsibleBox } from '@/components/common/collapsible-box.tsx';
import { CollapsibleGroup } from '@/components/common/collapsible-group';
import { useCollapsibleGroup } from '@/components/common/collapsible-group.context';
import SwipeableContributorItem from '@/components/common/swipeable-contributor-item.tsx';
import type { SessionDto } from '@/types/dtos/sessions/session.dto.ts';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto.ts';
import { useMemo, useState, type ReactNode, useEffect } from 'react';
import AddEditContributorModal from '@/components/events/detail/tabs/contributors/tab-actions/add-edit-contributor';
import UpdateContributorForm from '@/components/events/detail/tabs/contributors/update-contributor.form.tsx';
import RequestContributorMediaModal from '@/components/events/detail/tabs/contributors/request-contributor-media.modal.tsx';
import { ContributorInvitationIcon } from '@/components/events/detail/tabs/contributors/contributor-invitation-icon.tsx';
import { ContributorInvitationExpandButton } from '@/components/events/detail/tabs/contributors/contributor-invitation-expand-button.tsx';
import { ContributorInviteStatusPanel } from '@/components/events/detail/tabs/contributors/contributor-invite-status-panel.tsx';
import { getContributorSessionRoleGeneric, getContributorFlowRoles } from '@/utils/contributor-extraction.utils';
import { useEventStore } from '@/state/stores/use-event-store';
import ConfirmationModal from '@/components/common/confirmation-modal';
import { usePatchContributorRoleMutation } from '@/state/mutations/contributors/use-patch-contributor-role';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useCreateContributorRoleSlotMutation } from '@/state/mutations/contributors/use-create-contributor-role-slot';
import { useCreateSessionRoleSlotsForAllEventSlotsMutation } from '@/state/mutations/contributors/use-create-session-role-slots-all-event-slots';
import { useDeleteContributorRoleSlotMutation } from '@/state/mutations/contributors/use-delete-contributor-role-slot';
import { usePatchContributorRoleSlotMutation } from '@/state/mutations/contributors/use-patch-contributor-role-slot';
import { useQueryClient } from '@tanstack/react-query';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session';
import { getObjectId } from '@/helpers/object.id.helper';
import { useDeleteEntityContributor } from '@/hooks/use-delete-entity-contributor';
import { getSessionActorPublicationHints } from '@/utils/contributor-invite-publication.utils';
import type { ContributorListDisplayVariant } from '@/components/flows/detail/flow-detail.types';
import { ScheduleContributorHeaderAvatar } from './schedule-contributor-header-avatar';
import { getContributorProfileReviewIconColor } from './speaker-profile-review-status';
import { resolveContributorLineRemoteSpeakerStatus } from '@/utils/remote-speaker-status.utils';
import { contributorItemInlineStatusIcons } from '@/components/events/detail/tabs/common/contributors/contributor-item-inline-status-icons';
import { ContributorItemOverflowMenu } from '@/components/events/detail/tabs/common/contributors/contributor-item-overflow-menu';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import {
  buildPlaylistContributorRows,
  canReorderContributorPlaylist,
  getSessionCrewPlaylist,
  orderSessionCrewContributors,
} from '@/helpers/contributor-playlist.helper';
import { RemoteSpeakerConfigureModal } from '@/components/events/detail/tabs/common/contributors/remote-speaker-configure.modal';
import { RemoteSpeakerScopeModal } from '@/components/events/detail/tabs/common/contributors/remote-speaker-scope.modal';
import {
  RemoteSpeakerDayEntitlementModal,
  useRemoteSpeakerDayEntitlementModalState,
} from '@/components/events/detail/tabs/common/contributors/remote-speaker-day-entitlement.modal';
import {
  resolveFlowRemoteSpeakerConfigureEnabled,
  resolveFlowRemoteSpeakerMenuShowsRemote,
} from '@/utils/remote-speaker-flow-toggle.utils';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import { EVENT_PERMISSIONS } from '@/enums/permissions.enum';
import { getOriginEventId } from '@/helpers/event.helper';
import { SortablePlaylistContributorList } from '@/components/events/detail/tabs/common/contributors/sortable-playlist-contributor-list';
import {
  matchesSessionSegmentFlowSlotEntity,
  resolveContributorAnchorFlowSlotId,
} from '@/components/flows/detail/slot-list/session-segment.helpers';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { REMOTE_SPEAKERSHIP_FEATURE } from '@/constants/feature-flags';

export const SessionContributorList = ({
  session,
  displayVariant = 'default',
}: {
  session: SessionDto;
  displayVariant?: ContributorListDisplayVariant;
}) => {
  const [selectedContributor, setSelectedContributor] = useState<ContributorDto | undefined>();
  const [editModalProfileOnly, setEditModalProfileOnly] = useState(false);
  const [isEditContributorModalOpen, setIsEditContributorModalOpen] = useState(false);
  const [isRequestMediaModalOpen, setIsRequestMediaModalOpen] = useState(false);
  const [isToggleAutoCreateModalOpen, setIsToggleAutoCreateModalOpen] = useState(false);
  const [isFlowSlotModalOpen, setIsFlowSlotModalOpen] = useState(false);
  const [isToggleVisibilityModalOpen, setIsToggleVisibilityModalOpen] = useState(false);
  const [isRemoteSpeakerScopeModalOpen, setIsRemoteSpeakerScopeModalOpen] = useState(false);
  const [remoteSpeakerScopeEnabled, setRemoteSpeakerScopeEnabled] = useState(true);
  const [isSessionRemoteSpeakerModalOpen, setIsSessionRemoteSpeakerModalOpen] = useState(false);
  const [contributorToToggle, setContributorToToggle] = useState<ContributorDto | undefined>();
  const [flowSlotAction, setFlowSlotAction] = useState<'create' | 'delete' | null>(null);
  const [flowSlotToManage, setFlowSlotToManage] = useState<{
    roleId: string;
    slotId?: string;
    flowSlotId: string;
    eventSlotId?: string;
  } | null>(null);
  const [collapseContributorId, setCollapseContributorId] = useState<string | null>(null);
  const [confirmPending, setConfirmPending] = useState<{
    contributorId: string;
    action: 'visibility' | 'remote' | 'autoCreate' | 'flowSlot';
  } | null>(null);

  const isConfirmPending = (contributorId: string, action: 'visibility' | 'remote' | 'autoCreate' | 'flowSlot') =>
    confirmPending?.contributorId === contributorId && confirmPending.action === action;
  const contributors = useEventStore((s) => s.contributors);
  const event = useEventStore((s) => s.event);
  const eventPlan = useEventStore((s) => s.eventPlan);
  const originEventId = event ? getOriginEventId(event) : undefined;
  const { data: permissions } = usePermissionsQuery();
  const isEventManager =
    permissions?.events?.[originEventId ?? '']?.includes(EVENT_PERMISSIONS.MANAGE_EVENT_ROLES) ?? false;
  const { hasAccess: hasRemoteSpeakershipAccess, isLoading: isRemoteSpeakershipAccessLoading } =
    useFeatureAccess(REMOTE_SPEAKERSHIP_FEATURE);
  const remoteSpeakershipAvailable = !isRemoteSpeakershipAccessLoading && hasRemoteSpeakershipAccess;
  const remoteSpeakerDayEntitlementModal = useRemoteSpeakerDayEntitlementModalState();
  const eventSessionsPlan = useEventStore((s) => s.eventSessionsPlan);
  const eventSessionsOrganize = useEventStore((s) => s.eventSessionsOrganize);
  const queryClient = useQueryClient();

  // Helper to get session ID consistently (handles both string and object origins)
  const sessionId = useMemo(() => {
    return session.origin ? (typeof session.origin === 'string' ? session.origin : session.origin.id) : session.id;
  }, [session]);
  const { mutateAsync: patchContributorRole } = usePatchContributorRoleMutation();
  const { mutateAsync: createContributorRoleSlot } = useCreateContributorRoleSlotMutation();
  const { mutateAsync: createSessionRoleSlotsForAllEventSlots } = useCreateSessionRoleSlotsForAllEventSlotsMutation();
  const { mutateAsync: deleteContributorRoleSlot } = useDeleteContributorRoleSlotMutation();
  const { mutateAsync: patchContributorRoleSlot } = usePatchContributorRoleSlotMutation();
  const deleteEntityContributor = useDeleteEntityContributor();
  const selectedSlot = useFlowStore((s) => s.selectedSlot);
  const flowFromStore = useFlowStore((s) => s.flow);
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flowOrganize = useFlowStore((s) => s.flowOrganize);
  const notification = useNotifications();
  const t = useTranslations();
  const mode = useEventMode();
  const deleteDisabled = isOrganizeMode(mode);

  const isFlowSlotAssetsUi = displayVariant === 'flowSlotAssets';
  const flow = useMemo(
    () => (isFlowSlotAssetsUi ? (flowPlan ?? flowOrganize ?? flowFromStore) : flowFromStore),
    [isFlowSlotAssetsUi, flowPlan, flowOrganize, flowFromStore],
  );

  // Check if we're in flow context (viewing a session slot in a flow)
  const isFlowContext = !!selectedSlot && !!flow && selectedSlot.contentType === 'Session';
  const isFlowScheduleInlineUi = displayVariant === 'flowScheduleInline';

  const anchorFlowSlotId = useMemo(() => {
    if (!isFlowContext || !selectedSlot || !flow) return selectedSlot?.id;
    return resolveContributorAnchorFlowSlotId(flow, selectedSlot);
  }, [isFlowContext, selectedSlot, flow]);

  const handleEditContributor = (contributor: ContributorDto, profileOnlyModal = false) => {
    setSelectedContributor(contributor);
    setEditModalProfileOnly(profileOnlyModal);
    setIsEditContributorModalOpen(true);
  };

  // Flow context: find roleslot info for current flow slot
  const getFlowSlotInfo = (contributor: ContributorDto) => {
    if (!isFlowContext || !selectedSlot || !flow || !anchorFlowSlotId) return null;

    const sessionRole = getContributorSessionRoleGeneric(contributor, session);
    if (!sessionRole) return null;

    // Check if roleslot exists for this flow slot
    // Note: deleted slots should be filtered out by the backend formatter, but check as safety
    const existingSlot = sessionRole.slots?.find((slot) => {
      if (slot.entityType !== 'FlowSlot' || slot.deleted === true) {
        return false;
      }
      // Handle both string ID and object with id property
      const slotEntityId = typeof slot.entity === 'string' ? slot.entity : slot.entity?.id;
      if (!slotEntityId) return false;
      const matchesFlowSlot = matchesSessionSegmentFlowSlotEntity(flow, selectedSlot, anchorFlowSlotId, slotEntityId);
      if (!matchesFlowSlot) return false;
      if (flow.slot?.id && slot.eventSlotId) {
        return slot.eventSlotId === flow.slot.id || slot.eventSlotId === flow.slot.origin;
      }
      return true;
    });

    return {
      role: sessionRole,
      roleslot: existingSlot,
    };
  };

  const handleToggleAutoCreateClick = (e: React.MouseEvent, contributor: ContributorDto) => {
    e.stopPropagation();

    // In flow context, create/delete roleslot instead of toggling autoCreate
    if (isFlowContext) {
      const slotInfo = getFlowSlotInfo(contributor);
      if (!slotInfo || !selectedSlot || !anchorFlowSlotId) return;

      if (slotInfo.roleslot) {
        setFlowSlotAction('delete');
        setFlowSlotToManage({
          roleId: slotInfo.role.id,
          slotId: slotInfo.roleslot.id,
          flowSlotId: anchorFlowSlotId,
          eventSlotId: flow?.slot?.id,
        });
      } else {
        setFlowSlotAction('create');
        setFlowSlotToManage({
          roleId: slotInfo.role.id,
          flowSlotId: anchorFlowSlotId,
          eventSlotId: flow?.slot?.id,
        });
      }
      setContributorToToggle(contributor);
      setIsFlowSlotModalOpen(true);
    } else {
      // In session context, toggle autoCreate setting
      setContributorToToggle(contributor);
      setIsToggleAutoCreateModalOpen(true);
    }
  };

  const handleToggleAutoCreateConfirm = async () => {
    if (!contributorToToggle) return;

    const role = getContributorSessionRoleGeneric(contributorToToggle, session);
    if (!role) return;

    // Handle undefined as true (default value)
    const currentValue = role.autoCreateSlots !== false; // true if undefined or true
    const newValue = !currentValue; // Toggle to opposite

    setConfirmPending({ contributorId: contributorToToggle.id, action: 'autoCreate' });
    try {
      await patchContributorRole({
        contributorId: contributorToToggle.id,
        roleId: role.id,
        autoCreateSlots: newValue,
      });
      // Refetch contributors to ensure Zustand store is updated
      await queryClient.refetchQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY] });
      notification.notifySuccess(t('session.notification.toggleAutoCreateSlotsSuccess'));
      setIsToggleAutoCreateModalOpen(false);
      setContributorToToggle(undefined);
    } catch (error) {
      console.error(error);
      notification.notifyError(t('session.notification.toggleAutoCreateSlotsError'));
    } finally {
      setConfirmPending(null);
    }
  };

  const handleFlowSlotActionConfirm = async () => {
    if (!flowSlotToManage || !contributorToToggle || !flow) return;

    // Get the correct session ID to use for API calls (use origin if available)
    const sessionIdForApi = session.origin ? getObjectId(session.origin) : session.id;
    // Get the correct flow ID to use for API calls (use origin if available)
    const flowIdForApi = flow.origin ? getObjectId(flow.origin) : flow.id;

    setConfirmPending({ contributorId: contributorToToggle.id, action: 'flowSlot' });
    try {
      if (flowSlotAction === 'create') {
        if (!flowSlotToManage.eventSlotId) {
          // Master mode: create roleslots for all eventSlots
          await createSessionRoleSlotsForAllEventSlots({
            contributorId: contributorToToggle.id,
            roleId: flowSlotToManage.roleId,
            sessionId: sessionIdForApi,
            flowId: flowIdForApi,
          });
        } else {
          // Specific eventSlot mode: create roleslot for this eventSlot
          await createContributorRoleSlot({
            contributorId: contributorToToggle.id,
            roleId: flowSlotToManage.roleId,
            flowSlotId: flowSlotToManage.flowSlotId,
            eventSlotId: flowSlotToManage.eventSlotId,
          });
        }
        const displayName =
          contributorToToggle.profiles?.[0]?.displayName || contributorToToggle.email || 'Contributor';
        notification.notifySuccess(
          t('event.detail.contributors.notifications.createRoleSlotSuccess', { name: displayName }),
        );
      } else if (flowSlotAction === 'delete' && flowSlotToManage.slotId) {
        // For non-library sessions, use session context deletion (delete both role and roleSlot)
        if (session.isInLibrary === false) {
          await deleteEntityContributor({
            contributor: contributorToToggle,
            entityType: 'Session',
            entityId: sessionIdForApi,
          });
          // Trigger collapse of the item after removing from session
          setCollapseContributorId(contributorToToggle.id);
        } else {
          // For library sessions, only delete the roleSlot (existing behavior)
          await deleteContributorRoleSlot({
            contributorId: contributorToToggle.id,
            roleId: flowSlotToManage.roleId,
            slotId: flowSlotToManage.slotId,
          });
          const displayName =
            contributorToToggle.profiles?.[0]?.displayName || contributorToToggle.email || 'Contributor';
          notification.notifySuccess(t('session.roleslot.deleteRoleSlotSuccess', { name: displayName }));
          // Trigger collapse of the item after removing from slot
          setCollapseContributorId(contributorToToggle.id);
        }
      }
      setIsFlowSlotModalOpen(false);
      setFlowSlotToManage(null);
      setFlowSlotAction(null);
      setContributorToToggle(undefined);
    } catch (error) {
      console.error(error);
      const displayName =
        contributorToToggle?.profiles?.[0]?.displayName || contributorToToggle?.email || 'Contributor';
      notification.notifyError(
        flowSlotAction === 'create'
          ? t('event.detail.contributors.notifications.createRoleSlotError', { name: displayName })
          : session.isInLibrary === false
            ? t('session.contributors.removeError', { name: displayName })
            : t('event.detail.contributors.notifications.deleteRoleSlotError', { name: displayName }),
      );
    } finally {
      setConfirmPending(null);
    }
  };

  const handleToggleVisibilityConfirm = async () => {
    if (!contributorToToggle) return;

    setConfirmPending({ contributorId: contributorToToggle.id, action: 'visibility' });
    try {
      const sessionRole = getContributorSessionRoleGeneric(contributorToToggle, session);
      if (!sessionRole) return;

      if (isFlowContext) {
        // In flow context, toggle slot-level isVisible
        const slotInfo = getFlowSlotInfo(contributorToToggle);
        if (!slotInfo?.roleslot || !selectedSlot) return;

        const currentValue = slotInfo.roleslot.isVisible !== false; // Default to true
        const newValue = !currentValue;

        await patchContributorRoleSlot({
          contributorId: contributorToToggle.id,
          roleId: sessionRole.id,
          slotId: slotInfo.roleslot.id,
          isVisible: newValue,
        });

        await queryClient.refetchQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY] });
        await queryClient.refetchQueries({ queryKey: [GET_SESSION_QUERY_KEY] });

        const displayName =
          contributorToToggle.profiles?.[0]?.displayName || contributorToToggle.email || 'Contributor';
        notification.notifySuccess(
          newValue
            ? t('session.visibility.enabledNotification', { name: displayName })
            : t('session.visibility.disabledNotification', { name: displayName }),
        );
      } else {
        // In session context, toggle role-level isVisible
        const currentValue = sessionRole.isVisible !== false; // Default to true
        const newValue = !currentValue;

        await patchContributorRole({
          contributorId: contributorToToggle.id,
          roleId: sessionRole.id,
          isVisible: newValue,
        });

        await queryClient.refetchQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY] });
        await queryClient.refetchQueries({ queryKey: [GET_SESSION_QUERY_KEY] });

        const displayName =
          contributorToToggle.profiles?.[0]?.displayName || contributorToToggle.email || 'Contributor';
        notification.notifySuccess(
          newValue
            ? t('session.visibility.enabledNotification', { name: displayName })
            : t('session.visibility.disabledNotification', { name: displayName }),
        );
      }

      setIsToggleVisibilityModalOpen(false);
      setContributorToToggle(undefined);
    } catch (error) {
      console.error(error);
      const displayName =
        contributorToToggle?.profiles?.[0]?.displayName || contributorToToggle?.email || 'Contributor';
      notification.notifyError(t('session.visibility.toggleError', { name: displayName }));
    } finally {
      setConfirmPending(null);
    }
  };

  const handleToggleRemoteSpeakerClose = () => {
    setIsRemoteSpeakerScopeModalOpen(false);
    setIsSessionRemoteSpeakerModalOpen(false);
    setContributorToToggle(undefined);
    setConfirmPending(null);
  };

  const flowIdForRemoteSpeaker = flow?.origin ? getObjectId(flow.origin) : flow?.id;

  const sessionPlaylist = getSessionCrewPlaylist(session);
  const canReorderContributors = canReorderContributorPlaylist(sessionPlaylist);

  const orderedContributors = useMemo(
    () => orderSessionCrewContributors(session, contributors || []),
    [session, contributors],
  );

  const playlistRows = useMemo(
    () =>
      buildPlaylistContributorRows(
        sessionPlaylist,
        orderedContributors,
        (contributor) => getContributorSessionRoleGeneric(contributor, session)?.id,
      ),
    [sessionPlaylist, orderedContributors, sessionId],
  );

  const selectedContributorRole = useMemo(
    () => (selectedContributor ? getContributorSessionRoleGeneric(selectedContributor, session) : undefined),
    [selectedContributor, sessionId],
  );

  // Component to handle collapsing items from outside CollapsibleBox
  const CollapsibleGroupCollapseHandler = ({
    collapseContributorId,
    onCollapsed,
  }: {
    collapseContributorId: string | null;
    onCollapsed: () => void;
  }) => {
    const groupContext = useCollapsibleGroup();

    useEffect(() => {
      if (collapseContributorId && groupContext) {
        // Directly collapse by setting expandedId to null
        groupContext.setExpandedId(null);
        // Reset the collapse trigger after a short delay to ensure state update
        setTimeout(() => {
          onCollapsed();
        }, 100);
      }
    }, [collapseContributorId, groupContext, onCollapsed]);

    return null;
  };

  return (
    <>
      {orderedContributors.length === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, px: 1, py: 2 }}>
          <InterpreterModeOutlined sx={{ fontSize: 28, color: 'action.disabled' }} />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t('common.actorsEmpty')}
          </Typography>
        </Box>
      )}
      <CollapsibleGroup>
        <CollapsibleGroupCollapseHandler
          collapseContributorId={collapseContributorId}
          onCollapsed={() => setCollapseContributorId(null)}
        />
        {(() => {
          const renderContributor = (contributor: ContributorDto, dragHandle?: ReactNode) => {
            const sessionRole = getContributorSessionRoleGeneric(contributor, session);
            if (!sessionRole) return null;

            const flowSlotInfo = isFlowContext ? getFlowSlotInfo(contributor) : null;
            const sessionActorPubHints = isFlowContext
              ? getSessionActorPublicationHints({
                  sessionRef: session,
                  eventSessionsPlan,
                  eventPlan,
                  timeSlotRoleSlot: flowSlotInfo?.roleslot ?? null,
                  eventSessionsOrganize,
                })
              : null;
            const displayName =
              contributor.profiles[0]?.displayName || t('event.detail.contributors.unknownContributor');
            const scheduleInlineRow = isFlowScheduleInlineUi && isFlowContext;

            // Determine which acceptance status to show:
            // 1. Flow context: roleslot acceptance if exists, otherwise flow role acceptance if exists, otherwise session role acceptance
            // 2. Session list: flow role acceptance if exists, otherwise session role acceptance
            // 3. Flow role: always prioritize flow role acceptance if they have a flow role
            let iconStart: ReactNode;

            // Check if contributor has a flow role (always check this first if flow exists)
            const flowRoles = flow ? getContributorFlowRoles(contributor, flow.id) : [];
            const flowRole = flowRoles.length > 0 ? flowRoles[0] : null; // Get first flow role if exists

            const profileIconButtonSx = {
              'p': 0.25,
              'borderRadius': 1,
              '&:hover': { backgroundColor: 'action.hover' },
            } as const;

            if (isFlowContext && isFlowScheduleInlineUi) {
              iconStart = (
                <ScheduleContributorHeaderAvatar
                  contributor={contributor}
                  onClick={() => handleEditContributor(contributor, true)}
                />
              );
            } else if (isFlowContext) {
              const rs = flowSlotInfo?.roleslot;
              iconStart = (
                <ContributorInvitationExpandButton
                  itemId={contributor.id}
                  color={getContributorProfileReviewIconColor(contributor)}
                  sx={profileIconButtonSx}
                >
                  <ContributorInvitationIcon
                    contributor={contributor}
                    flowDetailsInvite={{
                      roleAccepted: Boolean(sessionRole.acceptedAt),
                      roleInvited: Boolean(sessionRole.invitedAt),
                      roleStagedForInvite: sessionRole.stagedForInvite === true,
                      slotInvited: Boolean(rs?.invitedAt),
                      slotAccepted: Boolean(rs?.acceptedAt),
                      slotStagedForInvite: rs?.stagedForInvite === true,
                    }}
                  />
                </ContributorInvitationExpandButton>
              );
            } else {
              // In session list: prioritize flow role acceptance if exists, otherwise session role acceptance
              if (flowRole) {
                // Show flow role acceptance
                iconStart = (
                  <ContributorInvitationExpandButton
                    itemId={contributor.id}
                    color={getContributorProfileReviewIconColor(contributor)}
                    sx={profileIconButtonSx}
                  >
                    <ContributorInvitationIcon
                      contributor={{
                        ...contributor,
                        acceptedAt: flowRole.acceptedAt,
                        invitedAt: flowRole.invitedAt,
                        email: contributor.email,
                      }}
                    />
                  </ContributorInvitationExpandButton>
                );
              } else {
                // Show session role acceptance
                iconStart = (
                  <ContributorInvitationExpandButton
                    itemId={contributor.id}
                    color={getContributorProfileReviewIconColor(contributor)}
                    sx={profileIconButtonSx}
                  >
                    <ContributorInvitationIcon
                      contributor={{
                        ...contributor,
                        acceptedAt: sessionRole.acceptedAt,
                        invitedAt: sessionRole.invitedAt,
                        email: contributor.email,
                      }}
                    />
                  </ContributorInvitationExpandButton>
                );
              }
            }

            // Get the correct session ID to use for API calls (use origin if available)
            const sessionIdForApi = session.origin ? getObjectId(session.origin) : session.id;

            const shouldEnableSwipe =
              !isFlowSlotAssetsUi &&
              // In session list: allow removing from session
              ((!isFlowContext && !!sessionIdForApi) ||
                // In flow-slot context: only allow swipe removal if a roleslot exists (i.e. "remove from this slot")
                (isFlowContext && !!flowSlotInfo?.roleslot));

            const remoteSpeakerConfigured = contributor.remoteSpeakerEnabled ?? false;
            const remoteLineStatus = resolveContributorLineRemoteSpeakerStatus({
              contributor,
              role: sessionRole,
              roleSlot: flowSlotInfo?.roleslot,
              event: event ?? eventPlan ?? undefined,
              eventSlotId: flowSlotInfo?.roleslot?.eventSlotId,
              useRoleAcceptance: !isFlowContext || !flowSlotInfo?.roleslot,
            });
            const menuShowsRemote = isFlowContext
              ? resolveFlowRemoteSpeakerMenuShowsRemote({
                  role: sessionRole,
                  roleSlot: flowSlotInfo?.roleslot,
                  useSlotContext: Boolean(flowSlotInfo?.roleslot),
                  contributorRemoteSpeakerEnabled: remoteSpeakerConfigured,
                })
              : remoteSpeakerConfigured;
            const visibilityEnabled =
              isFlowContext && flowSlotInfo?.roleslot
                ? flowSlotInfo.roleslot.isVisible !== false
                : sessionRole.isVisible !== false;
            const showRemoteAndVisibility = !isFlowContext || !!flowSlotInfo?.roleslot;
            const showRemoteSpeakerControls = remoteSpeakershipAvailable && showRemoteAndVisibility;
            const labelContext = isFlowContext && flowSlotInfo?.roleslot ? ('flowSlot' as const) : ('session' as const);
            const showFlowSlotDelete = isFlowContext && !!flowSlotInfo?.roleslot;

            const onToggleRemoteSpeaker = () => {
              setContributorToToggle(contributor);
              if (isFlowContext) {
                const enabling = resolveFlowRemoteSpeakerConfigureEnabled({
                  role: sessionRole,
                  roleSlot: flowSlotInfo?.roleslot,
                  useSlotContext: Boolean(flowSlotInfo?.roleslot),
                  contributorRemoteSpeakerEnabled: remoteSpeakerConfigured,
                });
                setRemoteSpeakerScopeEnabled(enabling);
                setIsRemoteSpeakerScopeModalOpen(true);
                return;
              }
              setIsSessionRemoteSpeakerModalOpen(true);
            };
            const onToggleVisibility = () => {
              setContributorToToggle(contributor);
              setIsToggleVisibilityModalOpen(true);
            };

            const onRemoteSpeakerStatusClick =
              showRemoteSpeakerControls &&
              isFlowContext &&
              remoteLineStatus.dayStatus === 'pending' &&
              remoteLineStatus.dayId
                ? () => {
                    remoteSpeakerDayEntitlementModal.openForContributorDay(
                      contributor,
                      remoteLineStatus.dayId,
                      eventPlan ?? event,
                    );
                  }
                : undefined;

            const inlineStatusIcons = contributorItemInlineStatusIcons({
              t,
              labelContext,
              showRemoteSpeaker: showRemoteSpeakerControls && remoteLineStatus.showIcon,
              remoteSpeakerEnabled: remoteLineStatus.showIcon,
              remoteSpeakerIconColor: remoteLineStatus.color ?? 'info',
              onRemoteSpeakerStatusClick,
              remoteSpeakerStatusClickable:
                showRemoteSpeakerControls && isFlowContext && remoteLineStatus.dayStatus === 'pending',
              showVisibility: showRemoteAndVisibility,
              visibilityEnabled,
              onToggleVisibility: showRemoteAndVisibility ? onToggleVisibility : undefined,
              remoteSpeakerLoading: isConfirmPending(contributor.id, 'remote'),
              visibilityLoading: isConfirmPending(contributor.id, 'visibility'),
              disabled: confirmPending?.contributorId === contributor.id,
            });

            const profileReviewAction = scheduleInlineRow ? (
              <Tooltip
                key="profile-review"
                title={t('session.scheduleActor.profileReviewTooltip')}
                placement="left"
                disableInteractive
              >
                <IconButton
                  size="small"
                  color={getContributorProfileReviewIconColor(contributor)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditContributor(contributor, true);
                  }}
                  sx={{
                    '& > svg': {
                      fontSize: '1.5rem',
                      width: '1.5rem',
                      height: '1.5rem',
                    },
                  }}
                >
                  <RateReviewOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null;

            const overflowMenu =
              showRemoteAndVisibility || !isFlowContext || showFlowSlotDelete ? (
                <ContributorItemOverflowMenu
                  labelContext={labelContext}
                  showAutoCreate={!isFlowContext}
                  autoCreateEnabled={sessionRole.autoCreateSlots !== false}
                  onToggleAutoCreate={() => {
                    setContributorToToggle(contributor);
                    setIsToggleAutoCreateModalOpen(true);
                  }}
                  showRemoteSpeaker={showRemoteSpeakerControls}
                  remoteSpeakerEnabled={menuShowsRemote}
                  onToggleRemoteSpeaker={onToggleRemoteSpeaker}
                  showVisibility={showRemoteAndVisibility}
                  visibilityEnabled={visibilityEnabled}
                  onToggleVisibility={onToggleVisibility}
                  showDelete={showFlowSlotDelete}
                  deleteAction={session.isInLibrary ? 'removeFromSlot' : 'delete'}
                  deleteTarget="session"
                  deleteDisabled={deleteDisabled}
                  onDelete={() => {
                    if (!anchorFlowSlotId || !flowSlotInfo?.roleslot) return;
                    setContributorToToggle(contributor);
                    setFlowSlotAction('delete');
                    setFlowSlotToManage({
                      roleId: sessionRole.id,
                      slotId: flowSlotInfo.roleslot.id,
                      flowSlotId: anchorFlowSlotId,
                      eventSlotId: flow?.slot?.id,
                    });
                    setIsFlowSlotModalOpen(true);
                  }}
                />
              ) : null;

            const headerActionButtons = overflowMenu
              ? [
                  {
                    icon: overflowMenu,
                    unwrapped: true,
                    testId: `contributor-overflow-${contributor.id}`,
                  },
                ]
              : [];

            const item = (
              <CollapsibleBox
                sx={{
                  m: 0, // Margin moved to SwipeableContributorItem wrapper
                  ...(isFlowContext && !flowSlotInfo?.roleslot
                    ? {
                        opacity: 0.5,
                        pointerEvents: 'auto', // Keep pointer events for the icon
                      }
                    : {}),
                }}
                id={contributor.id}
                title={displayName}
                onClick={scheduleInlineRow ? undefined : () => handleEditContributor(contributor, false)}
                iconStart={
                  dragHandle ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {dragHandle}
                      {iconStart}
                    </Box>
                  ) : (
                    iconStart
                  )
                }
                iconsEnd={[...inlineStatusIcons, ...(profileReviewAction ? [profileReviewAction] : [])]}
                actionButtons={headerActionButtons}
                hideCollapseExpandTooltip
                collapseIcon={
                  // In flow context without roleslot, show AddCircle icon in expand icon position
                  isFlowContext && !flowSlotInfo?.roleslot ? (
                    <Tooltip title={t('event.detail.contributors.createRoleSlot')} placement="left" disableInteractive>
                      <IconButton
                        size="small"
                        disableRipple
                        disableFocusRipple
                        disableTouchRipple
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleAutoCreateClick(e, contributor);
                        }}
                      >
                        <AddCircleOutline fontSize="small" color="action" />
                      </IconButton>
                    </Tooltip>
                  ) : undefined
                }
              >
                <UpdateContributorForm
                  contributor={contributor}
                  entityId={sessionIdForApi}
                  entityType="Session"
                  showEmailField={!scheduleInlineRow}
                  fieldsLayout={scheduleInlineRow ? 'scheduleActor' : isFlowSlotAssetsUi ? 'emailOnly' : 'full'}
                  onFlowSlotActionClick={undefined}
                  showFlowSlotAction={false}
                  flowSlotActionIcon={undefined}
                  flowSlotActionTooltip={undefined}
                  hideDeleteButton={isFlowContext}
                  suppressWidgetIconButtons={isFlowContext && !!flowSlotInfo?.roleslot}
                  inviteStatusPanel={
                    isFlowContext && !isFlowScheduleInlineUi ? (
                      <ContributorInviteStatusPanel
                        key={`${contributor.id}-${sessionRole.id}-${flowSlotInfo?.roleslot?.id ?? 'no-slot'}`}
                        contributor={contributor}
                        roleId={sessionRole.id}
                        roleInvited={Boolean(sessionRole.invitedAt)}
                        roleAccepted={Boolean(sessionRole.acceptedAt)}
                        publicationBlockContribution={sessionActorPubHints?.contribution}
                        publicationBlockTimeSlot={sessionActorPubHints?.timeSlot}
                        displayVariant={isFlowSlotAssetsUi ? 'flowSlotAssets' : 'default'}
                        sessionRef={session}
                        timeSlot={
                          session.isInLibrary === true
                            ? null
                            : flowSlotInfo?.roleslot
                              ? {
                                  roleSlotId: flowSlotInfo.roleslot.id,
                                  invited: Boolean(flowSlotInfo.roleslot.invitedAt),
                                  accepted: Boolean(flowSlotInfo.roleslot.acceptedAt),
                                }
                              : null
                        }
                      />
                    ) : undefined
                  }
                />
              </CollapsibleBox>
            );

            return shouldEnableSwipe ? (
              <SwipeableContributorItem
                key={contributor.id}
                contributor={contributor}
                sessionId={sessionIdForApi}
                // For flow-slot context we ONLY support roleslot removal (not flow removal), so don't pass flow
                roleId={isFlowContext && flowSlotInfo?.roleslot ? sessionRole.id : undefined}
                roleslotId={isFlowContext && flowSlotInfo?.roleslot ? flowSlotInfo.roleslot.id : undefined}
                sessionRoleId={sessionRole.id}
                session={isFlowContext ? session : undefined}
              >
                {item}
              </SwipeableContributorItem>
            ) : (
              <Box key={contributor.id} sx={{ m: '0.4rem 0' }}>
                {item}
              </Box>
            );
          };

          if (canReorderContributors && sessionPlaylist?.id && playlistRows.length >= 2) {
            return (
              <SortablePlaylistContributorList
                rows={playlistRows}
                playlistId={sessionPlaylist.id}
                canReorder={canReorderContributors}
              >
                {(row, dragHandle) => renderContributor(row.contributor, dragHandle)}
              </SortablePlaylistContributorList>
            );
          }

          return orderedContributors.map((contributor) => renderContributor(contributor));
        })()}
      </CollapsibleGroup>
      <AddEditContributorModal
        contributor={selectedContributor}
        open={isEditContributorModalOpen}
        setOpen={setIsEditContributorModalOpen}
        eventSlotId={isFlowContext ? flow?.slot?.id : undefined}
        profileOnly={editModalProfileOnly}
      />
      {selectedContributorRole && (
        <RequestContributorMediaModal
          entity={session}
          entityType="Session"
          contributor={selectedContributor}
          isMediaRequested={selectedContributorRole?.isMediaRequested ?? false}
          role={selectedContributorRole}
          open={isRequestMediaModalOpen}
          setOpen={setIsRequestMediaModalOpen}
        />
      )}
      {contributorToToggle && isFlowContext && flowSlotToManage && (
        <ConfirmationModal
          open={isFlowSlotModalOpen}
          isLoading={confirmPending?.action === 'flowSlot'}
          title={
            flowSlotAction === 'create'
              ? t('event.detail.contributors.createRoleSlotModalTitle', {
                  name:
                    contributorToToggle.profiles[0]?.displayName || t('event.detail.contributors.unknownContributor'),
                })
              : session.isInLibrary === false
                ? t('session.contributors.removeTitle', {
                    name:
                      contributorToToggle.profiles[0]?.displayName || t('event.detail.contributors.unknownContributor'),
                  })
                : t('event.detail.contributors.deleteRoleSlotModalTitle', {
                    name:
                      contributorToToggle.profiles[0]?.displayName || t('event.detail.contributors.unknownContributor'),
                  })
          }
          text={
            flowSlotAction === 'create'
              ? t('event.detail.contributors.createRoleSlotModalText', {
                  name:
                    contributorToToggle.profiles[0]?.displayName || t('event.detail.contributors.unknownContributor'),
                })
              : session.isInLibrary === false
                ? t('session.contributors.removeText', {
                    name:
                      contributorToToggle.profiles[0]?.displayName || t('event.detail.contributors.unknownContributor'),
                  })
                : t('event.detail.contributors.deleteRoleSlotModalText', {
                    name:
                      contributorToToggle.profiles[0]?.displayName || t('event.detail.contributors.unknownContributor'),
                  })
          }
          onConfirm={handleFlowSlotActionConfirm}
          onCancel={() => {
            if (confirmPending) return;
            setIsFlowSlotModalOpen(false);
            setFlowSlotToManage(null);
            setFlowSlotAction(null);
            setContributorToToggle(undefined);
          }}
        />
      )}
      {contributorToToggle &&
        !isFlowContext &&
        (() => {
          const roleToToggle = getContributorSessionRoleGeneric(contributorToToggle, session);
          const currentValue = roleToToggle?.autoCreateSlots !== false; // true if undefined or true
          const displayName =
            contributorToToggle.profiles[0]?.displayName || t('event.detail.contributors.unknownContributor');
          return (
            <ConfirmationModal
              open={isToggleAutoCreateModalOpen}
              isLoading={confirmPending?.action === 'autoCreate'}
              title={
                currentValue
                  ? t('session.autoCreateSlots.disableTitle', { name: displayName })
                  : t('session.autoCreateSlots.enableTitle', { name: displayName })
              }
              text={
                currentValue
                  ? t('session.autoCreateSlots.disableConfirmation', { name: displayName })
                  : t('session.autoCreateSlots.enableConfirmation', { name: displayName })
              }
              onConfirm={handleToggleAutoCreateConfirm}
              onCancel={() => {
                if (confirmPending) return;
                setIsToggleAutoCreateModalOpen(false);
                setContributorToToggle(undefined);
              }}
            />
          );
        })()}
      {contributorToToggle &&
        (() => {
          const sessionRole = getContributorSessionRoleGeneric(contributorToToggle, session);
          if (!sessionRole) return null;

          // Determine visibility value: in flow context use slot-level, otherwise role-level
          const slotInfo = isFlowContext ? getFlowSlotInfo(contributorToToggle) : null;
          const visibilityValue =
            isFlowContext && slotInfo?.roleslot
              ? slotInfo.roleslot.isVisible !== false
              : sessionRole.isVisible !== false;

          // Use context-specific confirmation messages and titles
          const displayName =
            contributorToToggle.profiles[0]?.displayName || t('event.detail.contributors.unknownContributor');
          const confirmationKey = isFlowContext
            ? visibilityValue
              ? 'session.visibility.disableConfirmationFlow'
              : 'session.visibility.enableConfirmationFlow'
            : visibilityValue
              ? 'session.visibility.disableConfirmationSession'
              : 'session.visibility.enableConfirmationSession';

          const titleKey = isFlowContext
            ? visibilityValue
              ? 'session.visibility.hideTitleFlow'
              : 'session.visibility.toggleTitleFlow'
            : visibilityValue
              ? 'session.visibility.hideTitleSession'
              : 'session.visibility.toggleTitleSession';

          return (
            <ConfirmationModal
              open={isToggleVisibilityModalOpen}
              isLoading={confirmPending?.action === 'visibility'}
              title={t(titleKey, { name: displayName })}
              text={t(confirmationKey, { name: displayName })}
              onConfirm={handleToggleVisibilityConfirm}
              onCancel={() => {
                if (confirmPending) return;
                setIsToggleVisibilityModalOpen(false);
                setContributorToToggle(undefined);
              }}
            />
          );
        })()}
      {contributorToToggle &&
        (() => {
          const sessionRole = getContributorSessionRoleGeneric(contributorToToggle, session);
          if (!sessionRole) return null;

          const slotInfo = isFlowContext ? getFlowSlotInfo(contributorToToggle) : null;

          if (remoteSpeakershipAvailable && isFlowContext && flowIdForRemoteSpeaker) {
            return (
              <RemoteSpeakerScopeModal
                open={isRemoteSpeakerScopeModalOpen}
                onClose={handleToggleRemoteSpeakerClose}
                contributor={contributorToToggle}
                enabled={remoteSpeakerScopeEnabled}
                flowId={flowIdForRemoteSpeaker}
                roleId={sessionRole.id}
                slotId={slotInfo?.roleslot?.id}
                showSlotOption={Boolean(slotInfo?.roleslot)}
              />
            );
          }

          const remoteSpeakerValue = sessionRole.effectiveRemoteSpeaker ?? sessionRole.isRemoteSpeaker ?? false;

          return remoteSpeakershipAvailable ? (
            <RemoteSpeakerConfigureModal
              open={isSessionRemoteSpeakerModalOpen}
              onClose={handleToggleRemoteSpeakerClose}
              contributor={contributorToToggle}
              enabled={!remoteSpeakerValue}
              scope="session"
              sessionId={session.id}
              roleId={sessionRole.id}
              flowId={flow?.id}
            />
          ) : null;
        })()}
      {remoteSpeakershipAvailable && remoteSpeakerDayEntitlementModal.contributor ? (
        <RemoteSpeakerDayEntitlementModal
          open={remoteSpeakerDayEntitlementModal.open}
          onClose={remoteSpeakerDayEntitlementModal.close}
          contributor={remoteSpeakerDayEntitlementModal.contributor}
          dayIds={remoteSpeakerDayEntitlementModal.dayIds}
          dayLabel={remoteSpeakerDayEntitlementModal.dayLabel}
          isEventManager={isEventManager}
        />
      ) : null}
    </>
  );
};
